<template>
  <div class="sign-in" :data-step="step">
    <div class="sign-in__heading">
      <h1 class="sign-in__title">Sign In</h1>
      <p v-if="step == 2">
        We`ve sent a code to <b>{{ email }}</b>
      </p>
    </div>
    <form class="sign-in__form" @submit.prevent="submitForm">
      <template v-if="step === 1">
        <UiInput
          v-model="email"
          type="username"
          placeholder="Email"
          clearable
          autofocus
          :disabled="isSending"
          :error-text="emailError"
        />
        <UiButton type="submit" variant="primary" stretch :loading="isSending">Continue</UiButton>
      </template>
      <template v-else>
        <UiCode
          v-model="code"
          length="6"
          autofocus
          :disabled="isSignIn"
          :error-text="codeError"
          @filled="signIn"
          @input="onInput"
        />
        <UiButton stretch :loading="isSending" :disabled="isTimerActive" @click="resendCode"
          >Resend Code <template v-if="isTimerActive">in {{ remaining }} sec.</template></UiButton
        >
      </template>
    </form>
  </div>
</template>
<script lang="ts" setup>
import { shallowRef, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useField } from 'vee-validate'
import * as yup from 'yup'
import { sendCode } from '@/api/auth'
import { useAsyncState, useCountdown } from '@vueuse/core'
import { getError } from '@/utils/error'
import { useAuthStore } from '@/stores/auth'
import { useHeader } from '@/composables/header'
import { useUserStore } from '@/stores/user'
import { useLayoutLoader } from '@/composables/loader'
import { APP_HOME_PAGE } from '@/constants'

const authStore = useAuthStore()
const { show: showLoader, hide: hideLoader } = useLayoutLoader()
const { fetch } = useUserStore()
const router = useRouter()
const route = useRoute()

const step = shallowRef(1)
const code = shallowRef('')
const codeError = shallowRef('')
const resendTimeoutSeconds = 60

const { remaining, start: startTimer, isActive: isTimerActive } = useCountdown(resendTimeoutSeconds)
const { setBackHandler, showBackButton } = useHeader()

// Form
const {
  value: email,
  errorMessage: emailError,
  validate: validateEmail,
  setErrors,
} = useField<string>('email', yup.string().email().required())

// Watchers
watch(step, (value) => {
  showBackButton.value = value == 2
})

// Methods
setBackHandler(() => {
  step.value = 1
  codeError.value = ''
})

async function submitForm() {
  const { valid } = await validateEmail()

  if (!valid) {
    return
  }

  sendVerificationCode()
}

function onInput() {
  codeError.value = ''
}

function resendCode() {
  sendVerificationCode()
  code.value = ''
  codeError.value = ''
}

const { executeImmediate: sendVerificationCode, isLoading: isSending } = useAsyncState(
  () =>
    sendCode({
      email: email.value,
    }).then(() => {
      step.value = 2
      startTimer()
    }),
  null,
  {
    immediate: false,
    onError(error) {
      setErrors(getError(error).message)
    },
  },
)

const { isLoading: isSignIn, executeImmediate: signIn } = useAsyncState(
  async () => {
    showLoader()
    await authStore.login(email.value, code.value)

    if (authStore.isAuthorized) {
      await fetch()
      const redirect = route.query.redirect as string
      router.replace(redirect || APP_HOME_PAGE)
    }
  },
  null,
  {
    immediate: false,
    onSuccess: hideLoader,
    onError(error) {
      hideLoader()
      codeError.value = getError(error).message
    },
  },
)

// extend route page
definePage({
  name: 'signin',
  meta: {
    requireAuth: false,
    disableBack: true,
  },
})
</script>
<style lang="sass" scoped>
@use '@/styles/variables'
@use "sass:math"

.sign-in
  &__heading
    display: flex
    flex-direction: column
    gap: variables.$spacer
    margin-bottom: variables.$spacer*5

  &__form
    display: flex
    flex-direction: column
    gap: variables.$spacer*2

    @at-root
      [data-step="2"] &
        width: fit-content
</style>
